import {
  BoxV2 as Box,
  Button,
  DropdownV2 as Dropdown,
  Flex,
  TextInput,
} from 'portal-commons';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faSquareMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useControllers } from './hooks';
import { BulkSuspendFormValues } from './types';
import Modal from '@/components/ModalV2';
import { SelectItem } from '@/utils/types';

const DefaultFormValues: BulkSuspendFormValues = {
  suspensionCategory: '',
  explanation: '',
};

interface BulkSuspendModalProps {
  open?: boolean;
  disabled?: boolean;
  campaignUids: string[];
  suspensionCategoryOptions: SelectItem[];
  onClose?: () => void;
  onSubmit?: (suspensionCategory: string, explanation: string) => void;
}

const BulkSuspendModal: FC<BulkSuspendModalProps> = ({
  open,
  disabled,
  campaignUids,
  suspensionCategoryOptions,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<BulkSuspendFormValues>({
    defaultValues: DefaultFormValues,
  });
  const {
    suspensionCategory,
    setSuspensionCategory,
    suspensionCategoryError,
    explanation,
    setExplanation,
    validateExplanation,
    explanationError,
  } = useControllers(control);

  const handleCancel = () => {
    onClose?.();
  };

  const handleValid = (value: BulkSuspendFormValues) => {
    onSubmit?.(value.suspensionCategory, value.explanation);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal
      data-testid="bulkSuspendModal"
      open={open}
      sx={{ padding: '24px 32px' }}
    >
      <form
        style={{ width: '100%' }}
        noValidate
        onSubmit={handleSubmit(handleValid)}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'xl',
            color: 't.black70',
            fontWeight: 400,
            lineHeight: 'normal',
          }}
        >
          <Flex
            sx={{
              alignItems: 'center',
              gap: 's',
            }}
          >
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                color: 't.black80',
                fontSize: '22px',
              }}
            >
              <FontAwesomeIcon icon={faSquareMinus} />
            </Flex>
            <Box sx={{ color: 't.black90', fontWeight: 600, fontSize: 'H600' }}>
              Suspend Campaigns
            </Box>
          </Flex>

          <Box sx={{ fontSize: 'H300' }}>
            {campaignUids.length} campaign(s) selected for suspension.
          </Box>

          <Flex sx={{ flexDirection: 'column', gap: 'm', width: '100%' }}>
            <Flex
              sx={{
                flexDirection: 'column',
                gap: 'xs',
                fontWeight: 600,
                fontSize: 'H400',
              }}
            >
              <Box sx={{ color: 't.black90' }}>Campaign ID(s)</Box>
              <Box>{campaignUids.join(', ')}</Box>
            </Flex>

            <Box sx={{ fontSize: 'H100' }}>* Indicates a Required Field</Box>
            <Dropdown
              data-testid="bulkSuspendModalSuspensionCategory"
              required
              disabled={disabled}
              error={!!suspensionCategoryError?.message}
              label="Campaign Suspension Category"
              helperText={suspensionCategoryError?.message}
              options={suspensionCategoryOptions}
              value={suspensionCategory}
              onChange={setSuspensionCategory}
            />
            <TextInput
              data-testid="bulkSuspendModalExplanation"
              required
              disabled={disabled}
              error={!!explanationError?.message}
              label="Explanation"
              note={explanationError?.message}
              value={explanation}
              onChange={setExplanation}
              onBlur={validateExplanation}
            />
          </Flex>

          <Flex sx={{ gap: '28px' }}>
            <Button
              data-testid="bulkSuspendModalCancelButton"
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid="bulkSuspendModalSubmitButton"
              type="submit"
              disabled={
                disabled ||
                Object.values(errors).some((value) => !!value.message)
              }
            >
              Suspend
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default BulkSuspendModal;
